import React, { useState } from "react";
import { Phone, PhoneCall, PhoneOff, Delete, Loader2, Mic, Volume2, Info } from "lucide-react";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";
import { Button } from "@/components/ui/button";

// -----------------------------------------------------------------------------
// IVR AI Autobot — mock UI. Real telephony (Twilio / Exotel) needs API keys
// which the user hasn't supplied yet. The dial-pad, call log and script
// preview all work locally so the flow can be validated end-to-end.
// -----------------------------------------------------------------------------
const KEYS = [
  ["1", "abc"], ["2", "def"],  ["3", "ghi"],
  ["4", "jkl"], ["5", "mno"],  ["6", "pqr"],
  ["7", "stu"], ["8", "vwx"],  ["9", "yz"],
  ["*", ""],    ["0", "+"],    ["#", ""],
];

const MENU = [
  { key: "1", label: "Find nearby services & shops" },
  { key: "2", label: "Property, rentals, matrimonial" },
  { key: "3", label: "Doctors, hospitals & pharmacies" },
  { key: "4", label: "Emergency SOS & alerts" },
  { key: "5", label: "Jobs, gigs & informal work" },
  { key: "6", label: "Speak to a human agent" },
  { key: "0", label: "Repeat menu / Change language" },
];

const SAMPLE_LOG = [
  { id: "c1", to: "1800-FIND-2NEAR", from: "You", at: "2 minutes ago", duration: "01:14", intent: "Nearby dentist", status: "resolved" },
  { id: "c2", to: "1800-FIND-2NEAR", from: "You", at: "yesterday",     duration: "00:42", intent: "PG rooms in Bangalore", status: "resolved" },
  { id: "c3", to: "1800-FIND-2NEAR", from: "You", at: "3 days ago",    duration: "02:31", intent: "Truck driver jobs",     status: "sent-to-agent" },
];

export default function IvrAutobot() {
  const [dial, setDial] = useState("1800-FIND-2NEAR");
  const [callState, setCallState] = useState("idle");      // idle | dialing | ringing | connected | ended
  const [selected, setSelected] = useState(null);
  const [muted, setMuted] = useState(false);
  const [log, setLog] = useState(SAMPLE_LOG);

  useSeo({ title: "IVR AI Autobot · Dial in and browse by voice · find2near",
           description: "Dial our AI autobot to browse listings, find services, book rides and speak with a human agent — all over a phone call.",
           canonical: "/ivr" });

  const press = (k) => { if (callState === "idle") setDial(d => (d.length < 24 ? d + k : d)); };
  const del   = ()  => setDial(d => d.slice(0, -1));

  const startCall = () => {
    if (callState !== "idle") return;
    setCallState("dialing");
    setTimeout(() => setCallState("ringing"),   700);
    setTimeout(() => setCallState("connected"), 1900);
  };
  const hangUp = () => {
    if (callState === "connected") {
      setLog(l => [{
        id: `c${Date.now()}`, to: dial, from: "You", at: "just now",
        duration: `00:${String(Math.floor(Math.random() * 45) + 15).padStart(2, "0")}`,
        intent: selected ? MENU.find(m => m.key === selected)?.label : "browse",
        status: selected === "6" ? "sent-to-agent" : "resolved",
      }, ...l]);
    }
    setCallState("ended");
    setSelected(null);
    setTimeout(() => setCallState("idle"), 900);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6" data-testid="ivr-page">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><PhoneCall size={12} className="text-emerald-600"/> IVR · AI autobot</div>
        <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Dial in. <span className="text-emerald-700">Browse by voice.</span></h1>
        <p className="text-stone-500 text-sm mt-1 max-w-xl">No app. No data. Just call and speak — our AI autobot understands 12+ Indian languages, connects you to the nearest service, and hands off to a human when needed.</p>
      </div>

      <div className="rounded-3xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 mb-5" data-testid="ivr-mock-banner">
        <Info size={14} className="text-amber-700 mt-0.5 shrink-0"/>
        <div className="text-xs text-amber-900">
          <b>Preview mode.</b> This is a working simulation of the IVR experience. To connect to a real
          number we need Twilio / Exotel API credentials (voice number + auth token). Add them to
          <code className="mx-1 px-1 bg-white rounded">backend/.env</code> and we'll flip this live.
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Phone / dial pad */}
        <section className="rounded-3xl bg-stone-950 text-white p-6 shadow-xl" data-testid="ivr-dialpad">
          <div className="text-[10px] uppercase tracking-widest text-stone-400 font-bold">Voice line</div>
          <div className="text-2xl font-mono font-bold min-h-[36px]" data-testid="ivr-number">{dial || <span className="text-stone-600">—</span>}</div>
          <div className="text-[11px] text-stone-500 mt-1">
            {callState === "idle"      && "Ready to dial"}
            {callState === "dialing"   && "Dialing…"}
            {callState === "ringing"   && "Ringing…"}
            {callState === "connected" && <span className="text-emerald-400 font-bold">● Connected — press menu</span>}
            {callState === "ended"     && "Call ended"}
          </div>

          {callState === "connected" ? (
            <div className="mt-4 space-y-1" data-testid="ivr-menu">
              {MENU.map(m => (
                <button
                  key={m.key}
                  onClick={() => setSelected(m.key)}
                  className={`w-full text-left rounded-2xl px-3 py-2 border transition-all ${selected === m.key ? "bg-emerald-600 border-emerald-500" : "bg-stone-900 border-stone-800 hover:border-stone-600"}`}
                  data-testid={`ivr-menu-${m.key}`}
                >
                  <span className="font-mono text-emerald-400 mr-2">{m.key}</span>
                  <span className="text-sm">{m.label}</span>
                </button>
              ))}
              {selected && (
                <div className="mt-3 rounded-2xl bg-emerald-950/60 border border-emerald-800 p-3 text-xs">
                  <b className="text-emerald-300">Bot says:</b> "You chose {MENU.find(m => m.key === selected)?.label.toLowerCase()}. Please tell me your city and preference after the beep."
                </div>
              )}
            </div>
          ) : (
            <div className="mt-4 grid grid-cols-3 gap-2" data-testid="ivr-keys">
              {KEYS.map(([n, sub]) => (
                <button
                  key={n}
                  onClick={() => press(n)}
                  className="rounded-2xl bg-stone-900 hover:bg-stone-800 border border-stone-800 py-3 flex flex-col items-center transition-transform active:scale-95"
                  data-testid={`ivr-key-${n}`}
                >
                  <span className="text-2xl font-black">{n}</span>
                  <span className="text-[9px] text-stone-500 uppercase tracking-widest">{sub}</span>
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-4 mt-5">
            {callState === "idle" || callState === "ended" ? (
              <button
                onClick={startCall}
                className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-400 flex items-center justify-center shadow-lg"
                data-testid="ivr-call-btn"
                aria-label="Start call"
              >
                <Phone size={26} className="text-white"/>
              </button>
            ) : (
              <>
                <button
                  onClick={() => setMuted(m => !m)}
                  className={`w-12 h-12 rounded-full ${muted ? "bg-stone-700" : "bg-stone-800"} border border-stone-700 flex items-center justify-center`}
                  aria-label="Mute" data-testid="ivr-mute-btn"
                >
                  <Mic size={16} className={muted ? "text-red-400" : "text-white"}/>
                </button>
                <button
                  onClick={hangUp}
                  className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center shadow-lg"
                  data-testid="ivr-hangup-btn"
                  aria-label="Hang up"
                >
                  <PhoneOff size={26} className="text-white"/>
                </button>
                <button
                  className="w-12 h-12 rounded-full bg-stone-800 border border-stone-700 flex items-center justify-center"
                  aria-label="Speaker"
                >
                  <Volume2 size={16} className="text-white"/>
                </button>
              </>
            )}
            {(callState === "idle") && (
              <button
                onClick={del}
                className="w-12 h-12 rounded-full bg-stone-800 border border-stone-700 flex items-center justify-center"
                aria-label="Delete" data-testid="ivr-del-btn"
              >
                <Delete size={16} className="text-white"/>
              </button>
            )}
          </div>
        </section>

        {/* Call log + script */}
        <aside className="space-y-4">
          <div className="rounded-3xl bg-white border border-stone-200 p-4" data-testid="ivr-log">
            <div className="text-[11px] uppercase tracking-widest font-black text-stone-600 mb-2">Recent calls</div>
            <ul className="divide-y divide-stone-100">
              {log.map(c => (
                <li key={c.id} className="py-2 flex items-start gap-2" data-testid={`ivr-log-${c.id}`}>
                  <PhoneCall size={13} className="text-emerald-600 mt-0.5"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-stone-900 truncate">{c.intent}</div>
                    <div className="text-[11px] text-stone-500">{c.at} · {c.duration}</div>
                  </div>
                  <span className={`text-[9px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${c.status === "resolved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{c.status.replace("-", " ")}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl bg-white border border-stone-200 p-4">
            <div className="text-[11px] uppercase tracking-widest font-black text-stone-600 mb-2">Bot script preview</div>
            <p className="text-sm text-stone-700 leading-relaxed">
              "Welcome to find2near. For English press 1. हिंदी के लिए 2 दबाएँ. இதற்கு 3.
              I can find shops, doctors, jobs and rentals near you. Speak or press a number after the beep."
            </p>
            <p className="text-[11px] text-stone-500 mt-2">
              Languages supported once live: English, हिंदी, தமிழ், తెలుగు, ಕನ್ನಡ, മലയാളം, বাংলা, मराठी, ગુજરાતી, ਪੰਜਾਬੀ, اردو, ଓଡ଼ିଆ.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
